import React, { useEffect, useState } from 'react'

import { Card } from 'components'
import { useLoading, useModal, usePersistForm, useToggle } from 'hooks'
import { useForm } from 'react-hook-form'
import {
  DjurServices,
  IGetPaginatedSearchDjur,
  ISearchDjurItems
} from 'services/NGT/DjurServices'
import { COLORS, FORMAT, SPACING } from 'utils'

import { IDetailsDjur } from '../../DJURService'
import { FilterTableServicesDjur, TableServicesDjur } from './components'

interface ISearchServicesDjur {
  handleDjurDetails: (_type: IDetailsDjur) => void
}

const DEFAULT_FORM: IGetPaginatedSearchDjur = {
  cdContrato: '',
  cervello: '',
  cnpjCpfPassaporte: '',
  cpfPassaporte: '',
  nomeFornecedor: '',
  numeroAtendimentoDjur: '',
  numeroProcesso: '',
  pageNumber: 1,
  pageSize: 15
}

const SearchServicesDjur: React.FC<ISearchServicesDjur> = ({
  handleDjurDetails
}) => {
  const [djurTable, setDjurTable] = useState<ISearchDjurItems[]>([])
  const { setLoading } = useLoading()
  const { showModal } = useModal()

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
    formState: { errors }
  } = useForm<IGetPaginatedSearchDjur>({
    defaultValues: DEFAULT_FORM
  })

  usePersistForm('atendimento-djur-ngt-form', {
    setValue,
    watch
  })

  const [page, setPage] = useState<number>(1)
  const [rowsPerPage, setRowsPerPage] = useState<number>(15)
  const [pageCount, setPageCount] = useState<number>(1)

  const handleChangePage = (newPage: number) => setPage(newPage)

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(1)
  }

  const handleCleanFields = () => {
    reset()
    handleClickSearchButton({
      ...getValues()
    })
  }

  const handleSearch = (data: IGetPaginatedSearchDjur) => {
    setLoading('loading')

    const unmaskedData: IGetPaginatedSearchDjur = {
      cdContrato: data.cdContrato,
      cervello: data.cervello,
      cnpjCpfPassaporte: FORMAT.unMask(data.cnpjCpfPassaporte),
      cpfPassaporte: FORMAT.unMask(data.cpfPassaporte),
      nomeFornecedor: data.nomeFornecedor,
      numeroAtendimentoDjur: data.numeroAtendimentoDjur,
      numeroProcesso: data.numeroProcesso,
      pageNumber: page,
      pageSize: rowsPerPage
    }

    DjurServices()
      .searchDjur(unmaskedData)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error('Não foi possível pesquisar.')
        }

        setDjurTable(response.data.items)
        setPageCount(response.data.pageCount)
        setLoading('idle')
      })
      .catch((error: any) => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message:
            error.mensagem || error.message || 'Não foi possível pesquisar.',
          txtButtonConfirm: 'Fechar',
          type: 'error'
        })
      })
  }

  const handleClickSearchButton = (data: IGetPaginatedSearchDjur) => {
    if (page === 1) {
      handleSearch({
        ...data,
        pageNumber: page,
        pageSize: rowsPerPage
      })
      return
    }

    setPage(1)
  }

  useEffect(() => {
    handleSearch({
      ...getValues(),
      pageNumber: page,
      pageSize: rowsPerPage
    })
  }, [page, rowsPerPage])

  return (
    <Card
      marginLeft={SPACING.spacing20}
      marginRight={SPACING.spacing20}
      marginBottom={SPACING.spacing100}
      padding={SPACING.spacing20}
      boxShadow={`0 0 1em ${COLORS.lightGray}`}
    >
      <FilterTableServicesDjur
        control={control}
        errors={errors}
        watch={watch}
        handleSubmit={handleSubmit(handleClickSearchButton)}
        handleCleanFields={handleCleanFields}
      />
      <TableServicesDjur
        tableData={FORMAT.orderByLast(djurTable)}
        handleDjurDetails={handleDjurDetails}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        handleChangePage={handleChangePage}
        page={page}
        pageCount={pageCount}
        rowsPerPage={rowsPerPage}
      />
    </Card>
  )
}

export default SearchServicesDjur
